import { runtimeConfig } from '@/config/runtime-config'
import { createApiClient } from '@/lib/api/http-client'
import { getStoredToken } from '@/lib/auth/token-storage'

export type ModelConfigValue =
  | boolean
  | number
  | string
  | null
  | ModelConfigValue[]
  | { [key: string]: ModelConfigValue }

export interface ModelConfigField {
  description: string
  field_type: string
  is_sensitive: boolean
  key: string
  label: string
  options: Record<string, string>[] | null
  placeholder: string
  required: boolean
  validation_pattern: string
}

export interface ModelInterfaceMeta {
  default_base_url?: string
  default_config: Record<string, ModelConfigValue>
  description: string
  fields: ModelConfigField[]
  key: string
  label: string
}

export interface ModelProviderInterfaceOption {
  default_base_url: string
  description?: string
  key: string
  label: string
}

export interface ModelProviderOption {
  interfaces: ModelProviderInterfaceOption[]
  key: string
  label: string
}

export interface ModelProviderMeta {
  interfaces: ModelInterfaceMeta[]
  providers: ModelProviderOption[]
  protocols: {
    description?: string
    key: string
    label: string
  }[]
}

export type RegisteredModelStatus = 'active' | 'disabled'
export type ModelProbeStatus = 'unknown' | 'healthy' | 'slow' | 'failed'
export type ModelFallbackTriggerMode = 'probe_only' | 'runtime_only' | 'probe_or_runtime'

export interface ModelProbeHistoryItem {
  checked_at?: string | null
  hour?: number
  label: string
  latency_ms?: number | null
  status: ModelProbeStatus
}

export interface ModelProbeSummary {
  config?: {
    enabled?: boolean
    interval_minutes?: number
    is_recovery_mode?: boolean
    latest_latency_ms?: number | null
    latest_message?: string
    latest_status?: ModelProbeStatus
    latest_throughput_tps?: number | null
    latest_ttft_ms?: number | null
    next_probe_at?: string | null
    recovery_interval_minutes?: number
    warning_latency_ms?: number
  } | null
  history?: ModelProbeHistoryItem[]
  latest_result?: {
    checked_at?: string | null
    latency_ms?: number | null
    message?: string
    output_tokens?: number | null
    status: ModelProbeStatus
    throughput_tps?: number | null
    trigger_type?: 'auto' | 'manual' | 'runtime' | null
    ttft_ms?: number | null
  }
}

export interface ModelProbeDetail extends ModelProbeSummary {
  fallback_summary?: ModelFallbackSummary
  model_id: string
  model_name: string
  provider: string
}

export interface ModelFallbackSummary {
  config: {
    enabled: boolean
    fallback_active: boolean
    trigger_mode: ModelFallbackTriggerMode
  }
  target_model?: {
    id: string
    interface_type: string
    model_name: string
    provider: string
    provider_label?: string
    status: RegisteredModelStatus
  } | null
}

export interface RegisteredModel {
  base_url: string
  config: Record<string, ModelConfigValue>
  created_at: string
  fallback_summary?: ModelFallbackSummary
  id: string
  input_price_per_million?: number | string | null
  interface_label?: string
  interface_type: string
  masked_config?: Record<string, ModelConfigValue> | null
  model_name: string
  output_price_per_million?: number | string | null
  pricing_currency?: string | null
  probe_summary?: ModelProbeSummary
  provider: string
  provider_label?: string
  public_model_name: string
  status: RegisteredModelStatus
  updated_at?: string
}

export interface RegisteredModelsListParams {
  page: number
  pageSize: number
  provider?: string
  search?: string
  status?: string
}

export interface RegisteredModelsListData {
  items: RegisteredModel[]
  page: number
  page_size: number
  total: number
}

export interface FallbackCandidatesData {
  items: RegisteredModel[]
}

export interface RegisteredModelFormValues {
  baseUrl: string
  config: Record<string, ModelConfigValue>
  fallbackEnabled: boolean
  fallbackTargetModelId: string
  fallbackTriggerMode: ModelFallbackTriggerMode
  inputPricePerMillion: string
  interfaceType: string
  modelName: string
  outputPricePerMillion: string
  pricingCurrency: string
  probeEnabled: boolean
  probeIntervalMinutes: string
  probeRecoveryIntervalMinutes: string
  probeWarningLatencyMs: string
  provider: string
  publicModelName: string
  status: RegisteredModelStatus
}

export interface FallbackCandidatesParams {
  currentTargetModelId?: string
  excludeModelId?: string | null
  interfaceType: string
}

interface SuccessEnvelope<T> {
  code: 'success'
  data: T
}

interface MessageEnvelope {
  code: 'success'
  message?: string
}

const apiClient = createApiClient({
  baseUrl: runtimeConfig.apiBaseUrl,
  getToken: () => getStoredToken('admin'),
  portal: 'admin',
})

function appendOptionalQuery(params: URLSearchParams, key: string, value: string | undefined) {
  const normalized = value?.trim()

  if (normalized) {
    params.set(key, normalized)
  }
}

function trimmedOrNull(value: string) {
  const normalized = value.trim()

  return normalized.length > 0 ? normalized : null
}

function modelPayload(values: RegisteredModelFormValues) {
  return {
    base_url: values.baseUrl.trim(),
    config: values.config,
    input_price_per_million: values.inputPricePerMillion.trim(),
    interface_type: values.interfaceType,
    model_name: values.modelName.trim(),
    output_price_per_million: values.outputPricePerMillion.trim(),
    pricing_currency: trimmedOrNull(values.pricingCurrency) ?? '',
    provider: values.provider,
    public_model_name: values.publicModelName.trim(),
    status: values.status,
  }
}

function fallbackPayload(values: RegisteredModelFormValues) {
  return {
    enabled: values.fallbackEnabled,
    target_registered_model_id: trimmedOrNull(values.fallbackTargetModelId),
    trigger_mode: values.fallbackTriggerMode,
  }
}

function probePayload(values: RegisteredModelFormValues) {
  return {
    enabled: values.probeEnabled,
    interval_minutes: Number(values.probeIntervalMinutes),
    recovery_interval_minutes: Number(values.probeRecoveryIntervalMinutes),
    warning_latency_ms: Number(values.probeWarningLatencyMs),
  }
}

export async function getModelProviderMeta() {
  const response = await apiClient.request<SuccessEnvelope<ModelProviderMeta>>(
    '/admin/model-provider/meta',
  )

  return response.data
}

export async function listRegisteredModels({
  page,
  pageSize,
  provider,
  search,
  status,
}: RegisteredModelsListParams) {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  })

  appendOptionalQuery(params, 'provider', provider)
  appendOptionalQuery(params, 'search', search)
  appendOptionalQuery(params, 'status', status)

  const response = await apiClient.request<SuccessEnvelope<RegisteredModelsListData>>(
    `/admin/model-provider?${params.toString()}`,
  )

  return response.data
}

export async function listFallbackCandidates({
  currentTargetModelId,
  excludeModelId,
  interfaceType,
}: FallbackCandidatesParams) {
  const params = new URLSearchParams({
    interface_type: interfaceType,
  })

  appendOptionalQuery(params, 'exclude_model_id', excludeModelId ?? undefined)
  appendOptionalQuery(params, 'current_target_model_id', currentTargetModelId)

  const response = await apiClient.request<SuccessEnvelope<FallbackCandidatesData>>(
    `/admin/model-provider/fallback-candidates?${params.toString()}`,
  )

  return response.data
}

export async function getRegisteredModelDetail(modelId: string) {
  const response = await apiClient.request<SuccessEnvelope<RegisteredModel>>(
    `/admin/model-provider/${encodeURIComponent(modelId)}`,
  )

  return response.data
}

export async function createRegisteredModel(values: RegisteredModelFormValues) {
  const response = await apiClient.request<SuccessEnvelope<RegisteredModel>>(
    '/admin/model-provider',
    {
      body: JSON.stringify(modelPayload(values)),
      method: 'POST',
    },
  )

  return response.data
}

export async function updateRegisteredModel(modelId: string, values: RegisteredModelFormValues) {
  const response = await apiClient.request<SuccessEnvelope<RegisteredModel>>(
    `/admin/model-provider/${encodeURIComponent(modelId)}`,
    {
      body: JSON.stringify(modelPayload(values)),
      method: 'PUT',
    },
  )

  return response.data
}

export async function updateModelFallbackConfig(
  modelId: string,
  values: RegisteredModelFormValues,
) {
  const response = await apiClient.request<SuccessEnvelope<ModelFallbackSummary>>(
    `/admin/model-provider/${encodeURIComponent(modelId)}/fallback`,
    {
      body: JSON.stringify(fallbackPayload(values)),
      method: 'PUT',
    },
  )

  return response.data
}

export async function getModelProbeDetail(modelId: string) {
  const response = await apiClient.request<SuccessEnvelope<ModelProbeDetail>>(
    `/admin/model-provider/${encodeURIComponent(modelId)}/probe`,
  )

  return response.data
}

export async function updateModelProbeConfig(modelId: string, values: RegisteredModelFormValues) {
  const response = await apiClient.request<SuccessEnvelope<ModelProbeDetail>>(
    `/admin/model-provider/${encodeURIComponent(modelId)}/probe`,
    {
      body: JSON.stringify(probePayload(values)),
      method: 'PUT',
    },
  )

  return response.data
}

export async function runRegisteredModelManualProbe(modelId: string) {
  const response = await apiClient.request<SuccessEnvelope<ModelProbeDetail>>(
    `/admin/model-provider/${encodeURIComponent(modelId)}/probe/manual`,
    {
      method: 'POST',
    },
  )

  return response.data
}

export async function deleteRegisteredModel(modelId: string) {
  return apiClient.request<MessageEnvelope>(
    `/admin/model-provider/${encodeURIComponent(modelId)}`,
    {
      method: 'DELETE',
    },
  )
}
